/**
 * Tenant slug resolver — single source of truth used by the axios interceptor
 * to auto-inject `?municipio_slug=...` into /v2/* requests.
 *
 * Resolution strategy:
 *   1. Read from `tenantSlugRef` (set by TenantContext via React Router).
 *   2. Defensive fallback: parse window.location.pathname for the brief
 *      window between page load and TenantProvider mount.
 *
 * The fallback uses a blocklist (`SYSTEM_ROUTES`) of known top-level routes
 * that look like slugs but aren't. New routes added to App.js whose first
 * segment is NOT a município slug must also be added here.
 *
 * Kept in its own module (no side effects, no axios import) so that unit
 * tests can require it without pulling the entire api.js graph.
 */
import { getTenantSlug } from './tenantSlugRef';

const SYSTEM_ROUTES = new Set([
  // Auth + onboarding
  'login', 'cadastrar', 'cadastrar-ong',
  'recuperar-senha', 'redefinir-senha',
  'verificar-email', 'verificar-2fa',
  // Admin areas (any sub-route under /admin is system)
  'admin',
  // User areas
  'perfil', 'meus-negocios', 'meus-favoritos', 'minhas-vagas',
  'completar-perfil', 'configuracoes',
  // Public top-level sections (not slugs, not nested under tenant)
  'explorar', 'vagas', 'noticias', 'promocoes', 'campanhas', 'coletivos', 'social',
  'negocios', 'busca', 'categorias', 'distrito', 'tags',
  'destaques', 'destaque', 'contratando', 'cupom',
  'contato', 'sobre', 'termos', 'privacidade', 'cookies', 'acessibilidade',
  // Auth callback / OAuth
  'auth',
  // API namespace (defensive)
  'api',
]);

// Prefixos UF (siglas de estado brasileiro) — quando a URL for /uf/slug,
// pulamos parts[1] e pegamos parts[2] como slug. IMPORTANTE: essas siglas
// NÃO podem estar em SYSTEM_ROUTES, senão o resolver retorna null cedo demais.
const UF_PREFIXES = new Set([
  'ac', 'al', 'ap', 'am', 'ba', 'ce', 'df', 'es', 'go', 'ma', 'mt', 'ms',
  'mg', 'pa', 'pb', 'pr', 'pe', 'pi', 'rj', 'rn', 'rs', 'ro', 'rr', 'sc',
  'sp', 'se', 'to',
  // Alias legacy "/m/<slug>" (URLs curtas antigas).
  'm',
]);

const MIN_SLUG_LENGTH = 3;

export function resolveSlugFromPath(pathname) {
  if (!pathname) return null;
  const parts = pathname.split('/');
  let candidate = parts[1];
  // Se o primeiro segmento é um prefixo UF (ex: /mg/ponte-nova), o slug real
  // fica em parts[2]. Sem isso, o interceptor não injeta municipio_slug e o
  // backend serve dados de TODOS os municípios (tenant leak).
  if (candidate && UF_PREFIXES.has(candidate.toLowerCase())) {
    candidate = parts[2];
  }
  if (!candidate) return null;
  const lower = candidate.toLowerCase();
  if (SYSTEM_ROUTES.has(lower)) return null;
  if (UF_PREFIXES.has(lower)) return null; // segurança extra: /mg/mg/... nunca é slug
  // Real slugs are kebab-case multi-char (e.g. "ponte-nova"). 1-2 char paths
  // are never legitimate municipality slugs and have caused 404 lookups.
  if (candidate.length < MIN_SLUG_LENGTH) return null;
  return candidate;
}

export function getMunicipioSlug() {
  const fromRef = getTenantSlug();
  if (fromRef) return fromRef;
  // SSR safety: window may be undefined in non-browser environments.
  if (typeof window === 'undefined' || !window.location) return null;
  return resolveSlugFromPath(window.location.pathname);
}

// Exported for tests so we can verify the contents of the blocklist.
export const __SYSTEM_ROUTES = SYSTEM_ROUTES;
